import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import KYCBanner from 'components/KYCBanner';
import MiniStatistics from 'components/card/MiniStatistics';
import { MdContentCopy, MdCheck, MdOutlinePeopleOutline, MdOutlineEmojiEvents, MdCheckCircle, MdRadioButtonUnchecked, MdArrowForward } from 'react-icons/md';
import { TbCoins, TbBrandWhatsapp, TbBrandLinkedin, TbBrandTwitter, TbMail } from 'react-icons/tb';
import { FiExternalLink } from 'react-icons/fi';

interface AffiliateStats {
    userCount: number;
    leadCount: number;
    totalDue: number;
}


interface CommissionConfig {
    level1: number;
    level2: number;
}

interface AffiliateRow {
    _id: string;
    name: string;
    email: string;
    leadCount: number;
    pricePerLead: number;
    totalDue: number;
}



interface StripeStatus {
    hasAccount: boolean;
    detailsSubmitted: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
}

const SellerAffiliate = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<any>(null);
    const [stats, setStats] = useState<AffiliateStats | null>(null);
    const [level2Stats, setLevel2Stats] = useState<AffiliateStats | null>(null);
    const [affiliates, setAffiliates] = useState<AffiliateRow[]>([]);
    const [level2Affiliates, setLevel2Affiliates] = useState<AffiliateRow[]>([]);
    const [commissionConfig, setCommissionConfig] = useState<CommissionConfig>({ level1: 0.90, level2: 0.10 });
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null);
    const [stripeLoading, setStripeLoading] = useState(true);
    const [kycLoading, setKycLoading] = useState(false);
    const [detailView, setDetailView] = useState<null | 'n1' | 'n2'>(null);
    const [walletBalance, setWalletBalance] = useState<number>(0);
    const [pendingRequest, setPendingRequest] = useState<{ requestedAt: string; amount: number } | null>(null);
    const [paymentLoading, setPaymentLoading] = useState(false);

    const authHeaders = () => ({
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
    });

    const initUser = () => {
        axios
            .get(`${CONFIG.apiUrl}/user/me`, authHeaders())
            .then((res) => {
                setUser(res.data);
                if (res.data && res.data.type !== 'seller') {
                    window.location.href = '/admin/dashboard';
                }
            })
            .catch(() => {})
            .finally(() => setUserLoading(false));
    };

    const initStats = () => {
        axios
            .get(`${CONFIG.apiUrl}/user/affiliate-stats`, authHeaders())
            .then((res) => {
                setStats(res.data?.summary ?? null);
                setLevel2Stats(res.data?.level2Summary ?? null);
                setAffiliates(res.data?.affiliates ?? []);
                setLevel2Affiliates(res.data?.level2Affiliates ?? []);
                if (res.data?.config) setCommissionConfig(res.data.config);
            })
            .catch(() => {
                setStats(null);
                setLevel2Stats(null);
                setAffiliates([]);
                setLevel2Affiliates([]);
            })
            .finally(() => setLoading(false));
    };

    const initStripeStatus = () => {
        axios
            .get(`${CONFIG.apiUrl}/stripe/account-status`, authHeaders())
            .then((res) => setStripeStatus(res.data))
            .catch(() => {})
            .finally(() => setStripeLoading(false));
    };

    const initPaymentStatus = () => {
        axios
            .get(`${CONFIG.apiUrl}/user/affiliate-payment-status`, authHeaders())
            .then((res) => {
                setWalletBalance(res.data.walletBalance ?? 0);
                setPendingRequest(res.data.pendingRequest ?? null);
            })
            .catch(() => {});
    };

    const requestPayment = () => {
        setPaymentLoading(true);
        axios
            .post(`${CONFIG.apiUrl}/user/affiliate-request-payment`, {}, authHeaders())
            .then((res) => {
                setPendingRequest({ requestedAt: new Date().toISOString(), amount: res.data.amount });
            })
            .catch(() => {})
            .finally(() => setPaymentLoading(false));
    };

    const startKyc = () => {
        setKycLoading(true);
        axios
            .get(`${CONFIG.apiUrl}/stripe/kyc`, authHeaders())
            .then((res) => { window.location.href = res.data.url; })
            .catch(() => setKycLoading(false));
    };

    useEffect(() => {
        if (!localStorage.getItem('token') || localStorage.getItem('token').length < 10) {
            window.location.href = '/auth/sign-up';
        }
        initUser();
        initStats();
        initStripeStatus();
        initPaymentStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasInvoicingInfo = !!(user?.siren && user?.company && user?.address);
    const isStripeOnboarded = !!(stripeStatus?.payoutsEnabled);
    // Dev uniquement : ?bypassGate dans l'URL ouvre la page sans infos de facturation ni Stripe.
    // NODE_ENV vaut 'production' dans le build (react-scripts build) : jamais actif en prod.
    const bypassGate = process.env.NODE_ENV === 'development' && new URLSearchParams(window.location.search).has('bypassGate');
    const isGateOpen = bypassGate || (!userLoading && !stripeLoading && hasInvoicingInfo && isStripeOnboarded);
    const gateReady = !userLoading && !stripeLoading;

    const affiliateLink = user?.affiliateCode
        ? `https://app.leadrs.co/auth/seller/sign-up?ref=${user.affiliateCode}`
        : null;

    const handleCopy = () => {
        if (!affiliateLink) return;
        navigator.clipboard.writeText(affiliateLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="flex flex-col gap-6">
            <KYCBanner user={user} onUserRefresh={initUser} />

            {/* Header */}
            <div className="relative overflow-hidden rounded-xl border border-leadrs-stroke bg-white p-6">
                <div className="relative z-10">
                    <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                        {"Programme d'affiliation"}
                    </p>
                    <h1 className="mt-1 font-crimson text-4xl font-semibold leading-tight text-leadrs-text">
                        Programme Ambassadeur
                    </h1>
                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                        {"Recommandez Leadrs a d'autres vendeurs et touchez une commission sur chaque lead vendu par vos filleuls."}
                    </p>
                </div>
                <div className="pointer-events-none absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-leadrs-purple-soft to-transparent opacity-40" />
            </div>

            {/* Gate overlay — shown until invoicing info + Stripe are complete */}
            {gateReady && !isGateOpen && (
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                        Accès au programme
                    </p>
                    <h2 className="mt-1 font-crimson text-2xl font-semibold text-leadrs-text">
                        Complétez votre profil pour débloquer l'affiliation
                    </h2>
                    <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                        Deux étapes sont requises avant de pouvoir partager votre lien et percevoir vos commissions.
                    </p>

                    <div className="mt-5 flex flex-col gap-3">
                        {/* Step 1 — invoicing info */}
                        <div className={`flex items-center justify-between rounded-xl border p-4 ${hasInvoicingInfo ? 'border-leadrs-green-soft bg-leadrs-green-soft' : 'border-leadrs-stroke bg-leadrs-bg'}`}>
                            <div className="flex items-center gap-3">
                                {hasInvoicingInfo
                                    ? <MdCheckCircle className="h-5 w-5 shrink-0 text-leadrs-green-focus" />
                                    : <MdRadioButtonUnchecked className="h-5 w-5 shrink-0 text-leadrs-text-soft" />
                                }
                                <div>
                                    <p className={`font-geist text-sm font-semibold ${hasInvoicingInfo ? 'text-leadrs-green-focus' : 'text-leadrs-text'}`}>
                                        Informations de facturation
                                    </p>
                                    <p className="font-geist text-xs text-leadrs-text-soft">
                                        Renseignez votre SIREN, raison sociale et adresse.
                                    </p>
                                </div>
                            </div>
                            {!hasInvoicingInfo && (
                                <button
                                    onClick={() => navigate('/admin/settings?tab=invoice')}
                                    className="flex shrink-0 items-center gap-1.5 rounded-lg bg-leadrs-brand px-4 py-2 font-geist text-sm font-semibold text-white transition-opacity hover:opacity-80"
                                >
                                    Compléter
                                    <MdArrowForward className="h-4 w-4" />
                                </button>
                            )}
                        </div>

                        {/* Step 2 — Stripe onboarding */}
                        <div className={`flex items-center justify-between rounded-xl border p-4 ${isStripeOnboarded ? 'border-leadrs-green-soft bg-leadrs-green-soft' : 'border-leadrs-stroke bg-leadrs-bg'}`}>
                            <div className="flex items-center gap-3">
                                {isStripeOnboarded
                                    ? <MdCheckCircle className="h-5 w-5 shrink-0 text-leadrs-green-focus" />
                                    : <MdRadioButtonUnchecked className="h-5 w-5 shrink-0 text-leadrs-text-soft" />
                                }
                                <div>
                                    <p className={`font-geist text-sm font-semibold ${isStripeOnboarded ? 'text-leadrs-green-focus' : 'text-leadrs-text'}`}>
                                        Compte Stripe activé
                                    </p>
                                    <p className="font-geist text-xs text-leadrs-text-soft">
                                        Connectez Stripe pour recevoir vos commissions par virement.
                                    </p>
                                </div>
                            </div>
                            {!isStripeOnboarded && (
                                <button
                                    onClick={startKyc}
                                    disabled={kycLoading}
                                    className="flex shrink-0 items-center gap-1.5 rounded-lg bg-leadrs-brand px-4 py-2 font-geist text-sm font-semibold text-white transition-opacity hover:opacity-80 disabled:opacity-50"
                                >
                                    {kycLoading ? 'Chargement...' : stripeStatus?.hasAccount ? "Continuer l'onboarding" : 'Créer mon compte'}
                                    {!kycLoading && <MdArrowForward className="h-4 w-4" />}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* KPI stats — blurred when gate is not open */}
            <div className={`contents ${!isGateOpen ? 'pointer-events-none' : ''}`}>
            <div className={`flex flex-col gap-6 transition-all ${gateReady && !isGateOpen ? 'select-none blur-sm' : ''}`}>

            {/* KPI stats */}
            <div className="grid grid-cols-2 gap-5 xl:grid-cols-4">
                <MiniStatistics
                    name="Filleuls directs (N1)"
                    value={loading ? '-' : String(stats?.userCount ?? 0)}
                    icon={<MdOutlinePeopleOutline color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Filleuls indirects (N2)"
                    value={loading ? '-' : String(level2Stats?.userCount ?? 0)}
                    icon={<MdOutlinePeopleOutline color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Leads valides (total)"
                    value={loading ? '-' : String((stats?.leadCount ?? 0) + (level2Stats?.leadCount ?? 0))}
                    icon={<TbCoins color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Commissions totales"
                    value={loading ? '-' : `${((stats?.totalDue ?? 0) + (level2Stats?.totalDue ?? 0)).toFixed(2)} €`}
                    icon={<MdOutlineEmojiEvents color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Affiliate link card */}
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <p className="font-geist text-sm font-semibold text-leadrs-text">
                        {"Votre lien d'affiliation"}
                    </p>
                    <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                        Partagez ce lien ou votre code pour inviter de nouveaux vendeurs.
                    </p>

                    {affiliateLink ? (
                        <>
                            {/* Code */}
                            <div className="mt-4 flex items-center justify-between rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                                <div>
                                    <p className="font-geist text-xs text-leadrs-text-soft">Votre code</p>
                                    <p className="font-geist text-lg font-bold tracking-widest text-leadrs-brand">
                                        {user?.affiliateCode}
                                    </p>
                                </div>
                                <button
                                    onClick={() => navigator.clipboard.writeText(user?.affiliateCode)}
                                    className="flex items-center gap-1.5 rounded-md border border-leadrs-stroke bg-white px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text transition-colors hover:bg-leadrs-bg"
                                >
                                    <MdContentCopy className="text-sm" />
                                    Copier
                                </button>
                            </div>

                            {/* Link */}
                            <div className="mt-2 flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                                <span className="flex-1 truncate font-geist text-xs text-leadrs-text-soft">
                                    {affiliateLink}
                                </span>
                                <button
                                    onClick={handleCopy}
                                    className="flex shrink-0 items-center gap-1.5 rounded-md bg-leadrs-brand px-3 py-1.5 font-geist text-xs font-semibold text-white transition-opacity hover:opacity-80"
                                >
                                    {copied ? (
                                        <>
                                            <MdCheck className="text-sm" />
                                            Copie !
                                        </>
                                    ) : (
                                        <>
                                            <MdContentCopy className="text-sm" />
                                            Copier le lien
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Social sharing */}
                            <div className="mt-4">
                                <p className="mb-2 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">
                                    Partager via
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <a
                                        href={`https://wa.me/?text=${encodeURIComponent(`Rejoignez Leadrs, la plateforme de vente de leads ! Inscrivez-vous avec mon lien : ${affiliateLink}`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-green-soft px-3 py-2 font-geist text-xs font-semibold text-leadrs-green-focus transition-opacity hover:opacity-80"
                                    >
                                        <TbBrandWhatsapp className="text-base" />
                                        WhatsApp
                                    </a>
                                    <a
                                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(affiliateLink)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-blue-soft px-3 py-2 font-geist text-xs font-semibold text-leadrs-blue-focus transition-opacity hover:opacity-80"
                                    >
                                        <TbBrandLinkedin className="text-base" />
                                        LinkedIn
                                    </a>
                                    <a
                                        href={`https://x.com/intent/tweet?text=${encodeURIComponent(`Je vends mes leads sur Leadrs, rejoignez-moi : ${affiliateLink}`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-3 py-2 font-geist text-xs font-semibold text-leadrs-text transition-opacity hover:opacity-80"
                                    >
                                        <TbBrandTwitter className="text-base" />
                                        X / Twitter
                                    </a>
                                    <a
                                        href={`mailto:?subject=${encodeURIComponent('Rejoignez Leadrs')}&body=${encodeURIComponent(`Bonjour,\n\nJe vous invite a rejoindre Leadrs, la plateforme de vente de leads.\n\nInscrivez-vous via mon lien : ${affiliateLink}\n\nA bientot !`)}`}
                                        className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-3 py-2 font-geist text-xs font-semibold text-leadrs-text transition-opacity hover:opacity-80"
                                    >
                                        <TbMail className="text-base" />
                                        Email
                                    </a>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="mt-4 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                            <p className="font-geist text-xs text-leadrs-text-soft">
                                {userLoading ? 'Chargement...' : 'Lien indisponible.'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Commissions card */}
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <p className="font-geist text-sm font-semibold text-leadrs-text">
                        Niveaux ambassadeur
                    </p>
                    <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                        Vous etes recompense sur deux niveaux pour chaque lead vendu par vos filleuls.
                    </p>
                    <div className="mt-4 flex flex-col gap-3">
                        {[
                            { level: 'Niveau 1', desc: 'Par lead vendu par un de vos filleuls directs', amount: commissionConfig.level1 },
                            { level: 'Niveau 2', desc: 'Par lead vendu par les filleuls de vos filleuls', amount: commissionConfig.level2 },
                        ].map((r) => (
                            <div key={r.level} className="flex items-center justify-between rounded-lg bg-leadrs-bg px-4 py-4">
                                <div>
                                    <p className="font-geist text-sm font-semibold text-leadrs-text">{r.level}</p>
                                    <p className="mt-0.5 font-geist text-xs text-leadrs-text-soft">{r.desc}</p>
                                </div>
                                <span className="ml-4 shrink-0 font-crimson text-2xl font-bold text-leadrs-brand">
                                    {r.amount.toFixed(2).replace('.', ',')} €
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Pyramid / detail card */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                {/* Card header — back button appears in detail view */}
                <div className="flex items-start justify-between">
                    <div>
                        <p className="font-geist text-sm font-semibold text-leadrs-text">Réseau ambassadeur</p>
                        <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                            {detailView === null
                                ? 'Visualisez votre pyramide de filleuls.'
                                : detailView === 'n1'
                                ? 'Filleuls directs — Niveau 1'
                                : 'Filleuls indirects — Niveau 2'}
                        </p>
                    </div>
                    {detailView !== null && (
                        <button
                            onClick={() => setDetailView(null)}
                            className="flex items-center gap-1 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text transition-colors hover:bg-white"
                        >
                            ← Retour
                        </button>
                    )}
                </div>

                {detailView === null ? (
                    <>
                        {/*
                            Pyramid — heights YOU=48px N1=112px N2=128px total=288px
                            Left edge: 50% × (1 − y/288) → seams at 42% (y=48) and 22% (y=160)
                            All tiers share the same diagonal slope → single straight line tip to base.
                        */}
                        <div className="mt-6 flex flex-col">
                            {/* Apex — pointed triangle */}
                            <div
                                className="flex h-12 items-end justify-center pb-2 bg-leadrs-brand"
                                style={{ clipPath: 'polygon(50% 0%, 58% 100%, 42% 100%)' }}
                            >
                                <p className="font-geist text-[10px] font-bold uppercase tracking-widest text-white">Vous</p>
                            </div>

                            {/* Niveau 1 */}
                            <div
                                className="flex h-32 flex-col items-center justify-center gap-0.5 bg-leadrs-purple-soft"
                                style={{ clipPath: 'polygon(42% 0%, 58% 0%, 78% 100%, 22% 100%)' }}
                            >
                                <p className="font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">Niveau 1</p>
                                <p className="font-crimson text-2xl font-bold text-leadrs-brand">
                                    {loading ? '…' : `${(stats?.totalDue ?? 0).toFixed(2)} €`}
                                </p>
                                <p className="font-geist text-xs text-leadrs-text-soft">
                                    {loading ? '' : `${stats?.userCount ?? 0} filleuls · ${stats?.leadCount ?? 0} leads`}
                                </p>
                                {!loading && (stats?.userCount ?? 0) > 0 && (
                                    <button
                                        onClick={() => setDetailView('n1')}
                                        className="mt-1 font-geist text-xs font-semibold text-leadrs-brand underline underline-offset-2 hover:opacity-70"
                                    >
                                        voir mes affiliés
                                    </button>
                                )}
                            </div>

                            {/* Niveau 2 */}
                            <div
                                className="flex h-36 flex-col items-center justify-center gap-0.5 bg-leadrs-blue-soft"
                                style={{ clipPath: 'polygon(22% 0%, 78% 0%, 100% 100%, 0% 100%)' }}
                            >
                                <p className="font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">Niveau 2</p>
                                <p className="font-crimson text-2xl font-bold text-leadrs-brand">
                                    {loading ? '…' : `${(level2Stats?.totalDue ?? 0).toFixed(2)} €`}
                                </p>
                                <p className="font-geist text-xs text-leadrs-text-soft">
                                    {loading ? '' : `${level2Stats?.userCount ?? 0} filleuls · ${level2Stats?.leadCount ?? 0} leads`}
                                </p>
                                {!loading && (level2Stats?.userCount ?? 0) > 0 && (
                                    <button
                                        onClick={() => setDetailView('n2')}
                                        className="mt-1 font-geist text-xs font-semibold text-leadrs-blue-focus underline underline-offset-2 hover:opacity-70"
                                    >
                                        voir mes affiliés
                                    </button>
                                )}
                            </div>
                        </div>

                        {!loading && affiliates.length === 0 && (
                            <p className="mt-6 text-center font-geist text-sm text-leadrs-text-soft">
                                Aucun filleul pour le moment. Partagez votre lien pour commencer !
                            </p>
                        )}
                    </>
                ) : (
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-left font-geist text-sm">
                            <thead>
                                <tr className="border-b border-leadrs-stroke">
                                    <th className="py-2 pr-4 font-medium text-leadrs-text-soft">#</th>
                                    <th className="py-2 pr-4 font-medium text-leadrs-text-soft">Vendeur</th>
                                    <th className="py-2 pr-4 text-right font-medium text-leadrs-text-soft">Leads validés</th>
                                    <th className="py-2 pr-4 text-right font-medium text-leadrs-text-soft">Prix / lead</th>
                                    <th className="py-2 text-right font-medium text-leadrs-text-soft">Commission</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(detailView === 'n1' ? affiliates : level2Affiliates).map((row, i) => (
                                    <tr key={row._id} className="border-t border-leadrs-stroke hover:bg-leadrs-bg">
                                        <td className="py-3 pr-4 text-xs text-leadrs-text-soft">#{i + 1}</td>
                                        <td className="py-3 pr-4">
                                            <p className="font-medium text-leadrs-text">{row.name || '—'}</p>
                                            <p className="text-xs text-leadrs-text-soft">{row.email}</p>
                                        </td>
                                        <td className="py-3 pr-4 text-right text-leadrs-text">{row.leadCount}</td>
                                        <td className="py-3 pr-4 text-right text-leadrs-text-soft">{row.pricePerLead?.toFixed(2)} €</td>
                                        <td className="py-3 text-right font-semibold text-leadrs-green-focus">
                                            {row.totalDue > 0 ? `${row.totalDue.toFixed(2)} €` : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Wallet payout CTA */}
            {!loading && walletBalance > 0 && (
                <div className={`rounded-xl border p-5 ${pendingRequest ? 'border-amber-200 bg-amber-50' : walletBalance >= 50 ? 'border-leadrs-green-soft bg-leadrs-green-soft' : 'border-leadrs-stroke bg-white'}`}>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                                Solde disponible
                            </p>
                            <p className="mt-1 font-crimson text-3xl font-bold text-leadrs-brand">
                                {walletBalance.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </p>
                            {pendingRequest ? (
                                <p className="mt-1 font-geist text-xs text-amber-600">
                                    Demande de virement en cours — {pendingRequest.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € demandés le{' '}
                                    {new Date(pendingRequest.requestedAt).toLocaleDateString('fr-FR')}
                                </p>
                            ) : walletBalance < 50 ? (
                                <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                    Minimum 50 € requis pour demander un virement ({(50 - walletBalance).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € manquants).
                                </p>
                            ) : (
                                <p className="mt-1 font-geist text-xs text-leadrs-green-focus">
                                    Votre solde est éligible au virement.
                                </p>
                            )}
                        </div>
                        {!pendingRequest && walletBalance >= 50 && (
                            <button
                                onClick={requestPayment}
                                disabled={paymentLoading}
                                className="flex shrink-0 items-center gap-2 rounded-lg bg-leadrs-brand px-5 py-2.5 font-geist text-sm font-semibold text-white transition-opacity hover:opacity-80 disabled:opacity-50"
                            >
                                {paymentLoading ? 'Envoi...' : 'Demander mon virement'}
                            </button>
                        )}
                        {pendingRequest && (
                            <span className="flex shrink-0 items-center gap-2 rounded-lg border border-amber-300 bg-amber-100 px-4 py-2 font-geist text-sm font-semibold text-amber-700">
                                En attente de traitement
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* CTA to share */}
            {affiliateLink && (
                <div className="flex items-center justify-between rounded-xl border border-leadrs-stroke bg-white p-5">
                    <div>
                        <p className="font-geist text-sm font-semibold text-leadrs-text">
                            Pret a inviter vos contacts ?
                        </p>
                        <p className="mt-0.5 font-geist text-xs text-leadrs-text-soft">
                            Chaque nouveau vendeur inscrit via votre lien vous fait progresser dans le programme.
                        </p>
                    </div>
                    <a
                        href={`mailto:?subject=Rejoignez%20Leadrs&body=Inscrivez-vous%20sur%20Leadrs%20via%20mon%20lien%20%3A%20${encodeURIComponent(affiliateLink)}`}
                        className="flex shrink-0 items-center gap-2 rounded-lg bg-leadrs-brand px-4 py-2 font-geist text-sm font-semibold text-white transition-opacity hover:opacity-80"
                    >
                        <FiExternalLink />
                        Inviter par email
                    </a>
                </div>
            )}
            </div>{/* end blur wrapper */}
            </div>{/* end pointer-events wrapper */}
        </div>
    );
};

export default SellerAffiliate;
